import React, { useMemo } from "react"
import { useQuery } from "@apollo/client/react"
import {
  ReactFlow,
  ReactFlowProvider,
  Controls,
  Background,
  BackgroundVariant,
  MarkerType
} from "@xyflow/react"
import "@xyflow/react/dist/style.css"

import {
  GetWorkflowExecutionDocument,
  GetWorkflowExecutionQuery,
  GetWorkflowExecutionQueryVariables
} from "lib/types/generated/graphql"
import { WorkflowNode, WorkflowEdge } from "lib/types/workflow"

import { nodeTypes } from "./Canvas/nodes"
import ExecutionEdge from "./Canvas/edges/ExecutionEdge"
import { OTHERWISE, THEN } from "./branchKind"
import {
  executionToFlow,
  ExecutionNodeShape
} from "helpers/executionConverter"

import { CanvasContainer, LoadingContainer } from "./ExecutionCanvas.styled"

const edgeTypes = { execution: ExecutionEdge }

type Props = {
  executionId: string
}

const ExecutionCanvasInner = ({ executionId }: Props) => {
  const { data, loading, error } = useQuery<
    GetWorkflowExecutionQuery,
    GetWorkflowExecutionQueryVariables
  >(GetWorkflowExecutionDocument, {
    variables: { executionId }
  })

  const { nodes, edges } = useMemo<{
    nodes: WorkflowNode[]
    edges: WorkflowEdge[]
  }>(() => {
    const triggerNode = data?.Order?.workflowExecution?.node as
      | ExecutionNodeShape
      | undefined
    if (!triggerNode) return { nodes: [], edges: [] }
    return executionToFlow(triggerNode)
  }, [data])

  const displayEdges = useMemo<WorkflowEdge[]>(
    () =>
      edges.map((e) => ({
        ...e,
        type: "execution",
        markerEnd: { type: MarkerType.ArrowClosed },
        animated: true,
        data: {
          branchLabel:
            e.sourceHandle === "true-output"
              ? THEN
              : e.sourceHandle === "false-output"
                ? OTHERWISE
                : undefined
        }
      })),
    [edges]
  )

  const memoNodeTypes = useMemo(() => nodeTypes, [])
  const memoEdgeTypes = useMemo(() => edgeTypes, [])

  if (loading) return <LoadingContainer>Loading execution…</LoadingContainer>
  if (error) return <LoadingContainer>Failed to load execution</LoadingContainer>
  if (nodes.length === 0)
    return <LoadingContainer>No execution data available</LoadingContainer>

  return (
    <CanvasContainer>
      <ReactFlow<WorkflowNode, WorkflowEdge>
        nodes={nodes}
        edges={displayEdges}
        nodeTypes={memoNodeTypes}
        edgeTypes={memoEdgeTypes}
        fitView
        fitViewOptions={{ padding: 0.25 }}
        nodesDraggable={false}
        nodesConnectable={false}
        edgesReconnectable={false}
        elementsSelectable={false}
        panOnDrag
        zoomOnScroll
        zoomOnPinch
        proOptions={{ hideAttribution: true }}
      >
        <Controls showInteractive={false} />
        <Background variant={BackgroundVariant.Dots} gap={16} size={1} />
      </ReactFlow>
    </CanvasContainer>
  )
}

const ExecutionCanvas = (props: Props) => (
  <ReactFlowProvider>
    <ExecutionCanvasInner {...props} />
  </ReactFlowProvider>
)

export default ExecutionCanvas
